import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CONTAINER_MIN_WIDTH as HEX_FIELD_MIN_WIDTH, FIELD_MIN_WIDTH as RGB_FIELD_MIN_WIDTH, useColorPickerDimensions } from '.';
import { useIsRTL } from '../../../locale';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { Button } from '../../Button';
import { useDirectSpeech } from '../../DirectSpeech/DirectSpeechProvider';
import { Input } from '../../Input';
import { Flow } from '../../Layouts/Flow';
import { Slider } from '../../Slider';
import { withLabels } from '../../withLabels';
import { basicColors } from './BasicColorPallete';
import { ColorIndicator } from './ColorIndicator';
import { useColorValidationAdapter, validateHexField, validateRGBField } from './hooks/useColorValidationAdapter';
import { useCustomColorPickerA11yInfo } from './hooks/useCustomColorPickerA11yInfo';
import { convertHexToRGB, convertHSVToRGB, convertRGBToHex, convertRGBToHSV, hydrateColorState } from './utils/ColorPickerUtil';
import cn from './CustomColorPicker.css.json';
import './CustomColorPicker.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InputWithLabel = withLabels(Input);
export const CustomColorPicker = props => {
  const localize = useNCTextLocalizer('ColorPicker');
  const validNewColor = props.newColor.color === '' ? props.lastSelectedColor : props.newColor;
  const isMultiColor = validNewColor?.type === 'automatic' && props.renderAutomaticSwatch;
  const [color, setColor] = useState(hydrateColorState(isMultiColor ? '#000000' : validNewColor?.color));
  const [canvasBackground, setCanvasBackground] = useState(color.hex);
  const [canvasThumbPos, setCanvasThumbPos] = useState({
    x: 0,
    y: 0
  });
  const colorCanvasRef = useRef(null);
  const canvasThumbRef = useRef(null);
  const canvasThumbIsDragging = useRef(false);
  const isRTL = useIsRTL();
  const directSpeech = useDirectSpeech();
  const redFieldRef = useRef(null);
  const greenFieldRef = useRef(null);
  const blueFieldRef = useRef(null);
  const hexFieldRef = useRef(null);
  const {
    calculateDimensions,
    fieldWidth: rgbFieldWidth,
    containerWidth: hexFieldWidth,
    wrapperRef: rgbContainerRef,
    labelContainerRef: greenLabelContainerRef
  } = useColorPickerDimensions();
  const currentColor = props.selectedColor?.color.toUpperCase();
  const currentColorType = props.selectedColor?.type;
  const newColor = color.hex;
  const newColorType = useRef(isMultiColor ? 'standard' : validNewColor?.type);
  const canvasBackgroundGradient = `${!isRTL ? 'linear-gradient(to right,#000000FF,transparent)' : 'linear-gradient(to left,#000000FF,transparent)'},linear-gradient(to bottom,${canvasBackground},transparent)`;
  const getCustomColorPickerA11yInfo = useCustomColorPickerA11yInfo();
  const {
    canvasThumbAriaLabel,
    hueSliderThumbAriaLabel
  } = getCustomColorPickerA11yInfo(currentColor, currentColorType, newColor, newColorType.current);
  const checkAnyFieldHasError = () => {
    const rgbFieldHasError = redFieldValidator.error.isError || greenFieldValidator.error.isError || blueFieldValidator.error.isError;
    const hexFieldHasError = hexFieldValidator.error.isError;
    return {
      rgbFieldHasError,
      hexFieldHasError
    };
  };
  const processRGBFieldChange = colorName => val => {
    let hex, hsv;
    const parsedValue = parseInt(val, 10);
    const {
      rgbFieldHasError,
      hexFieldHasError
    } = checkAnyFieldHasError();
    let isLiveFieldValueBlank = false;
    if (rgbFieldHasError) {
      setColor(prevState => ({
        ...prevState,
        [colorName]: parsedValue
      }));
    } else {
      switch (colorName) {
        case 'r':
          hex = convertRGBToHex(parsedValue, color.g, color.b);
          hsv = convertRGBToHSV(parsedValue, color.g, color.b);
          isLiveFieldValueBlank = redFieldValidator.value === '';
          break;
        case 'g':
          hex = convertRGBToHex(color.r, parsedValue, color.b);
          hsv = convertRGBToHSV(color.r, parsedValue, color.b);
          isLiveFieldValueBlank = greenFieldValidator.value === '';
          break;
        case 'b':
          hex = convertRGBToHex(color.r, color.g, parsedValue);
          hsv = convertRGBToHSV(color.r, color.g, parsedValue);
          isLiveFieldValueBlank = blueFieldValidator.value === '';
          break;
      }
      const {
        hue,
        saturation,
        value
      } = hsv;
      if (hex !== color.hex || isLiveFieldValueBlank) {
        hex !== color.hex && updateCanvasThumbPosition(saturation, value);
        setColor(prevState => ({
          ...prevState,
          [colorName]: parsedValue,
          h: hue,
          s: saturation,
          v: value,
          hex
        }));
        hexFieldValidator.setValue(hex.slice(1));
        hexFieldHasError && resetInputFieldErrorState();
        newColorType.current = !basicColors.includes(hex) ? 'custom' : 'standard';
        props.hideCustomTabActionButtons ? props.onColorSelect({
          color: hex,
          type: newColorType.current
        }) : props.onNewColorChange?.({
          color: hex,
          type: newColorType.current
        });
      }
    }
  };
  const processHexFieldChange = val => {
    const hex = `#${val}`;
    const {
      red,
      green,
      blue
    } = convertHexToRGB(hex);
    const {
      hue,
      saturation,
      value
    } = convertRGBToHSV(red, green, blue);
    const {
      rgbFieldHasError
    } = checkAnyFieldHasError();
    hex !== color.hex && updateCanvasThumbPosition(saturation, value);
    setColor(prevState => ({
      ...prevState,
      r: red,
      g: green,
      b: blue,
      h: hue,
      s: saturation,
      v: value,
      hex
    }));
    setInputFieldState({
      red,
      green,
      blue,
      hex
    });
    rgbFieldHasError && resetInputFieldErrorState();
    newColorType.current = !basicColors.includes(hex) ? 'custom' : 'standard';
    props.hideCustomTabActionButtons ? props.onColorSelect({
      color: hex,
      type: newColorType.current
    }) : props.onNewColorChange?.({
      color: hex,
      type: newColorType.current
    });
  };
  const redFieldValidator = useColorValidationAdapter({
    initialValue: color.r.toString(),
    validateInputFunction: validateRGBField,
    onCommit: processRGBFieldChange('r')
  });
  const greenFieldValidator = useColorValidationAdapter({
    initialValue: color.g.toString(),
    validateInputFunction: validateRGBField,
    onCommit: processRGBFieldChange('g')
  });
  const blueFieldValidator = useColorValidationAdapter({
    initialValue: color.b.toString(),
    validateInputFunction: validateRGBField,
    onCommit: processRGBFieldChange('b')
  });
  const hexFieldValidator = useColorValidationAdapter({
    initialValue: color.hex.slice(1),
    validateInputFunction: validateHexField,
    onCommit: processHexFieldChange,
    adapterType: 'HEX'
  });
  const setInputFieldState = values => {
    const {
      red,
      green,
      blue,
      hex
    } = values;
    redFieldValidator.setValue(red.toString());
    greenFieldValidator.setValue(green.toString());
    blueFieldValidator.setValue(blue.toString());
    hexFieldValidator.setValue(hex.slice(1));
  };
  const resetInputFieldErrorState = () => {
    const noError = {
      isError: false,
      message: undefined,
      status: undefined
    };
    redFieldValidator.setError(noError);
    greenFieldValidator.setError(noError);
    blueFieldValidator.setError(noError);
    hexFieldValidator.setError(noError);
  };

  // Calculate Saturation and Value
  const calculateSV = positionProps => {
    const {
      touchX: x,
      touchY: y,
      canvasWidth,
      canvasHeight
    } = positionProps;
    let iX;
    if (isRTL) {
      iX = (1 - x / canvasWidth) * 100;
    } else {
      iX = x / canvasWidth * 100;
    }
    const iY = (1 - y / canvasHeight) * 100;
    return {
      saturation: Math.round(iY),
      value: Math.round(iX)
    };
  };
  const getMousePointerPosition = e => {
    const canvasWidth = e.currentTarget.getBoundingClientRect().width;
    const canvasHeight = e.currentTarget.getBoundingClientRect().height;
    const canvasLeft = e.currentTarget.getBoundingClientRect().left;
    const canvasTop = e.currentTarget.getBoundingClientRect().top;
    const canvasThumbWidth = canvasThumbRef.current?.getBoundingClientRect().width;
    const canvasThumbHeight = canvasThumbRef.current?.getBoundingClientRect().height;
    let event;
    if ('touches' in e) {
      event = e.changedTouches[0];
    } else {
      event = e;
    }
    let touchX = event.clientX - canvasLeft;
    let touchY = event.clientY - canvasTop;
    touchX = Math.round(Math.min(Math.max(touchX, 0), canvasWidth));
    touchY = Math.round(Math.min(Math.max(touchY, 0), canvasHeight));
    const thumbX = Math.round(touchX - canvasThumbWidth / 2);
    const thumbY = Math.round(touchY - canvasThumbHeight / 2);
    return {
      thumbX,
      thumbY,
      touchX,
      touchY,
      canvasWidth,
      canvasHeight
    };
  };
  const processSVChanges = e => {
    const pos = getMousePointerPosition(e);
    const {
      saturation,
      value
    } = calculateSV(pos);
    const {
      red,
      green,
      blue
    } = convertHSVToRGB(color.h, saturation, value);
    const hex = convertRGBToHex(red, green, blue);
    setColor(prevState => ({
      ...prevState,
      r: red,
      g: green,
      b: blue,
      s: saturation,
      v: value,
      hex
    }));
    setInputFieldState({
      red,
      green,
      blue,
      hex
    });
    setCanvasThumbPos({
      x: pos.thumbX,
      y: pos.thumbY
    });
    newColorType.current = !basicColors.includes(hex) ? 'custom' : 'standard';
  };
  const onKeyDown = e => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    const canvasWidth = colorCanvasRef.current?.getBoundingClientRect().width;
    const canvasHeight = colorCanvasRef.current?.getBoundingClientRect().height;
    const canvasThumbWidth = canvasThumbRef.current?.getBoundingClientRect().width;
    const canvasThumbHeight = canvasThumbRef.current?.getBoundingClientRect().height;
    const {
      x: thumbLeft,
      y: thumbTop
    } = canvasThumbPos;
    let x = thumbLeft + canvasThumbWidth / 2,
      y = thumbTop + canvasThumbHeight / 2,
      preventDefault = true;
    switch (e.key) {
      case 'ArrowLeft':
        x -= 1;
        break;
      case 'ArrowRight':
        x += 1;
        break;
      case 'ArrowUp':
        y -= 1;
        break;
      case 'ArrowDown':
        y += 1;
        break;
      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      e.stopPropagation();
      e.preventDefault();
    }
    x = Math.min(Math.max(0, x), canvasWidth);
    y = Math.min(Math.max(0, y), canvasHeight);
    setCanvasThumbPos({
      x: x - canvasThumbWidth / 2,
      y: y - canvasThumbHeight / 2
    });
    const {
      saturation,
      value
    } = calculateSV({
      touchX: x,
      touchY: y,
      canvasWidth,
      canvasHeight
    });
    const {
      red,
      green,
      blue
    } = convertHSVToRGB(color.h, saturation, value);
    const hex = convertRGBToHex(red, green, blue);
    setInputFieldState({
      red,
      green,
      blue,
      hex
    });
    setColor(prevState => ({
      ...prevState,
      r: red,
      g: green,
      b: blue,
      s: saturation,
      v: value,
      hex
    }));
    const {
      rgbFieldHasError,
      hexFieldHasError
    } = checkAnyFieldHasError();
    (rgbFieldHasError || hexFieldHasError) && resetInputFieldErrorState();
    newColorType.current = !basicColors.includes(hex) ? 'custom' : 'standard';
  };
  const onKeyUp = e => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
    props.hideCustomTabActionButtons ? props.onColorSelect({
      color: color.hex,
      type: newColorType.current
    }) : props.onNewColorChange?.({
      color: color.hex,
      type: newColorType.current
    });
  };

  // Color canvas mouse event handlers
  const mouseDownHandler = e => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    canvasThumbIsDragging.current = true;
    processSVChanges(e);
    canvasThumbRef.current?.focus({
      preventScroll: true
    });
    (checkAnyFieldHasError().rgbFieldHasError || checkAnyFieldHasError().hexFieldHasError) && resetInputFieldErrorState();
  };
  const mouseMoveHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    if (canvasThumbIsDragging.current) {
      processSVChanges(e);
    }
  };
  const mouseLeaveHandler = mouseMoveHandler;

  // Color canvas touch event handlers
  const touchStartHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.changedTouches && e.changedTouches.length > 0) {
      processSVChanges(e);
      canvasThumbRef.current?.focus({
        preventScroll: true
      });
      (checkAnyFieldHasError().rgbFieldHasError || checkAnyFieldHasError().hexFieldHasError) && resetInputFieldErrorState();
    }
  };
  const touchMoveHandler = touchStartHandler;
  const touchEndHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    props.hideCustomTabActionButtons ? props.onColorSelect({
      color: color.hex,
      type: newColorType.current
    }) : props.onNewColorChange?.({
      color: color.hex,
      type: newColorType.current
    });
  };
  const hueSliderChangeHandler = e => {
    const {
      red,
      green,
      blue
    } = convertHSVToRGB(e.value, color.s, color.v);
    const hex = convertRGBToHex(red, green, blue);
    setColor(prevState => ({
      ...prevState,
      r: red,
      g: green,
      b: blue,
      h: e.value,
      hex
    }));
    setInputFieldState({
      red,
      green,
      blue,
      hex
    });
    newColorType.current = !basicColors.includes(hex) ? 'custom' : 'standard';
    props.hideCustomTabActionButtons ? props.onColorSelect({
      color: hex,
      type: newColorType.current
    }) : props.onNewColorChange?.({
      color: hex,
      type: newColorType.current
    });
    const selectedColor = !basicColors.includes(hex) ? hex.split('').join(' ') : `${localize(`color.${hex.slice(1)}.aria`)}, ${hex.split('').join(' ')}`;
    const directSpeechContent = localize('customTab.slider.colorSelect.msg', {
      color: selectedColor
    });
    directSpeech?.notify(directSpeechContent);
    (checkAnyFieldHasError().rgbFieldHasError || checkAnyFieldHasError().hexFieldHasError) && resetInputFieldErrorState();
  };
  const handleOkPress = () => {
    switch (true) {
      case redFieldValidator.error.isError:
        redFieldRef.current?.focus();
        return;
      case greenFieldValidator.error.isError:
        greenFieldRef.current?.focus();
        return;
      case blueFieldValidator.error.isError:
        blueFieldRef.current?.focus();
        return;
      case hexFieldValidator.error.isError:
        hexFieldRef.current?.focus();
        return;
    }
    props.onColorSelect?.({
      color: color.hex,
      type: newColorType.current
    });
  };
  const updateCanvasThumbPosition = (saturation, value) => {
    const canvasWidth = colorCanvasRef.current?.getBoundingClientRect().width;
    const canvasHeight = colorCanvasRef.current?.getBoundingClientRect().height;
    const canvasThumbWidth = canvasThumbRef.current?.getBoundingClientRect().width;
    const canvasThumbHeight = canvasThumbRef.current?.getBoundingClientRect().height;
    let centerX;
    centerX = Math.round(value * canvasWidth / 100);
    if (isRTL) {
      centerX = Math.round(canvasWidth - centerX);
    }
    const centerY = Math.round((1 - saturation / 100) * canvasHeight);
    const thumbLeft = Math.round(Math.max(centerX, 0) - canvasThumbWidth / 2);
    const thumbTop = Math.round(Math.max(centerY, 0) - canvasThumbHeight / 2);
    setCanvasThumbPos({
      x: thumbLeft,
      y: thumbTop
    });
  };
  useLayoutEffect(() => {
    updateCanvasThumbPosition(color.s, color.v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRTL]);

  // update color canvas background on hue change
  const updateColorCanvasBackground = useCallback(hue => {
    const {
      red,
      green,
      blue
    } = convertHSVToRGB(hue, 100, 100);
    const hex = convertRGBToHex(red, green, blue);
    setCanvasBackground(hex);
  }, []);
  useLayoutEffect(() => {
    updateColorCanvasBackground(color.h);
  }, [color.h, updateColorCanvasBackground]);
  useEffect(() => {
    redFieldValidator.preservedValue.current = color.r.toString();
    greenFieldValidator.preservedValue.current = color.g.toString();
    blueFieldValidator.preservedValue.current = color.b.toString();
    hexFieldValidator.preservedValue.current = color.hex.slice(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color.r, color.g, color.b, color.hex]);
  useEffect(() => {
    const mouseUpHandler = e => {
      if (e.button !== 0) return;
      if (canvasThumbIsDragging.current) {
        canvasThumbIsDragging.current = false;
        props.hideCustomTabActionButtons ? props.onColorSelect({
          color: color.hex,
          type: newColorType.current
        }) : props.onNewColorChange?.({
          color: color.hex,
          type: newColorType.current
        });
      }
    };
    window.addEventListener('mouseup', mouseUpHandler);
    return () => window.removeEventListener('mouseup', mouseUpHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color.hex, props.hideCustomTabActionButtons]);
  useEffect(() => {
    if (props.newColor?.color === '') {
      props.hideCustomTabActionButtons ? props.onColorSelect({
        color: newColor,
        type: newColorType.current
      }) : props.onNewColorChange?.({
        color: newColor,
        type: newColorType.current
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.newColor?.color]);
  const dt = useDesignTokens();
  let canvasThumbFocus;
  if (dt.themeVariant === 'light') {
    canvasThumbFocus = 'var(--focus-dark-bg)';
  } else {
    canvasThumbFocus = 'var(--focus-light-bg)';
  }
  const canvasTokens = renderInlineCssVars(
  // the black and white colors are hardcoded intentionally
  {
    'focus-outline': canvasThumbFocus,
    bd: '#000000',
    'inner-bd': '#ffffff'
  }, 'canvas-thumb');
  const tokens = renderInlineCssVars({
    'canvas-bd': dt.tokens['color-container-border-4'],
    'spacing-s': dt.tokens['space-base-s'],
    'spacing-m': dt.tokens['space-base-m'],
    'no-color-mark': dt.tokens['color-status-alert'],
    'canvas-bg': '#ffffff' // color hardcoded intentionally
  }, 'custom-color-picker');
  return /*#__PURE__*/_jsxs(Flow, {
    id: props.id,
    role: "tabpanel",
    "data-testid": postfixTestId(props.testId, 'container'),
    orientation: "vertical",
    className: cn.container,
    style: {
      ...tokens
    },
    children: [/*#__PURE__*/_jsx(ColorIndicator, {
      currentColor: {
        color: currentColor,
        type: currentColorType
      },
      newColor: {
        color: newColor,
        type: newColorType.current
      },
      renderAutomaticSwatch: props.renderAutomaticSwatch
    }), /*#__PURE__*/_jsxs(Flow, {
      "data-testid": postfixTestId(props.testId, 'canvasSliderContainer'),
      className: cn.canvasSliderContainer,
      spacing: "none",
      children: [/*#__PURE__*/_jsx(Flow, {
        ref: colorCanvasRef,
        "data-testid": postfixTestId(props.testId, 'colorCanvas'),
        className: cn.colorCanvas,
        spacing: "none",
        style: {
          backgroundImage: canvasBackgroundGradient,
          ...canvasTokens
        },
        onMouseDown: mouseDownHandler,
        onMouseMove: mouseMoveHandler,
        onMouseLeave: mouseLeaveHandler,
        onTouchStart: touchStartHandler,
        onTouchMove: touchMoveHandler,
        onTouchEnd: touchEndHandler,
        onScroll: e => {
          e.preventDefault();
          e.stopPropagation();
          // prevent thumb from getting scrolled on focus
          e.currentTarget.scrollLeft = 0;
          e.currentTarget.scrollTop = 0;
        },
        children: /*#__PURE__*/_jsx(Flow, {
          role: "img",
          tabIndex: 0,
          ref: canvasThumbRef,
          "aria-label": canvasThumbAriaLabel,
          "data-testid": postfixTestId(props.testId, 'canvasThumb'),
          onKeyUp: onKeyUp,
          onKeyDown: onKeyDown,
          className: cn.canvasThumb,
          style: {
            left: canvasThumbPos.x,
            top: canvasThumbPos.y
          }
        })
      }), /*#__PURE__*/_jsx(Flow, {
        spacing: "none",
        children: /*#__PURE__*/_jsx(Slider, {
          testId: postfixTestId(props.testId, 'hueSlider'),
          ariaLabel: hueSliderThumbAriaLabel,
          hideValueLabel: true,
          trackClassName: cn.hueSliderTrack,
          scale: [0, 360],
          value: color.h,
          orientation: "vertical",
          arrowGrips: true,
          onChange: hueSliderChangeHandler
        })
      })]
    }), /*#__PURE__*/_jsxs(Flow, {
      ref: () => {
        calculateDimensions();
      },
      orientation: "vertical",
      spacing: "m",
      "data-testid": postfixTestId(props.testId, 'inputFieldsContainer'),
      children: [/*#__PURE__*/_jsxs(Flow, {
        ref: rgbContainerRef,
        orientation: "horizontal",
        spacing: "xs",
        children: [/*#__PURE__*/_jsx(InputWithLabel, {
          ref: redFieldRef,
          testId: postfixTestId(props.testId, 'redField'),
          label: localize('customTab.redField.label'),
          labelPosition: "top",
          maxLength: 3,
          fieldWidth: rgbFieldWidth,
          fieldMinWidth: RGB_FIELD_MIN_WIDTH,
          commitOnEnterInDialog: true,
          labelSpacing: {
            topAligned: 'xs'
          },
          value: redFieldValidator.value,
          message: redFieldValidator.error?.message,
          status: redFieldValidator.error?.status,
          onChange: redFieldValidator.handleChange,
          onBlur: () => {
            if (canvasThumbIsDragging.current) return;
            redFieldValidator.handleBlur();
          },
          onCommit: redFieldValidator.handleCommit,
          onCancel: redFieldValidator.handleCancel
        }), /*#__PURE__*/_jsx(InputWithLabel, {
          labelContainerRef: greenLabelContainerRef,
          ref: greenFieldRef,
          testId: postfixTestId(props.testId, 'greenField'),
          label: localize('customTab.greenField.label'),
          labelPosition: "top",
          maxLength: 3,
          fieldWidth: rgbFieldWidth,
          fieldMinWidth: RGB_FIELD_MIN_WIDTH,
          commitOnEnterInDialog: true,
          labelSpacing: {
            topAligned: 'xs'
          },
          value: greenFieldValidator.value,
          message: greenFieldValidator.error?.message,
          status: greenFieldValidator.error?.status,
          onChange: greenFieldValidator.handleChange,
          onBlur: () => {
            if (canvasThumbIsDragging.current) return;
            greenFieldValidator.handleBlur();
          },
          onCommit: greenFieldValidator.handleCommit,
          onCancel: greenFieldValidator.handleCancel
        }), /*#__PURE__*/_jsx(InputWithLabel, {
          ref: blueFieldRef,
          testId: postfixTestId(props.testId, 'blueField'),
          label: localize('customTab.blueField.label'),
          labelPosition: "top",
          maxLength: 3,
          fieldWidth: rgbFieldWidth,
          fieldMinWidth: RGB_FIELD_MIN_WIDTH,
          commitOnEnterInDialog: true,
          labelSpacing: {
            topAligned: 'xs'
          },
          value: blueFieldValidator.value,
          message: blueFieldValidator.error?.message,
          status: blueFieldValidator.error?.status,
          onChange: blueFieldValidator.handleChange,
          onBlur: () => {
            if (canvasThumbIsDragging.current) return;
            blueFieldValidator.handleBlur();
          },
          onCommit: blueFieldValidator.handleCommit,
          onCancel: blueFieldValidator.handleCancel
        })]
      }), /*#__PURE__*/_jsx(InputWithLabel, {
        ref: hexFieldRef,
        testId: postfixTestId(props.testId, 'hexField'),
        label: localize('customTab.hexField.label'),
        labelPosition: "top",
        maxLength: 6,
        fieldWidth: hexFieldWidth,
        fieldMinWidth: HEX_FIELD_MIN_WIDTH,
        commitOnEnterInDialog: true,
        labelSpacing: {
          topAligned: 'xs'
        },
        value: hexFieldValidator.value,
        message: hexFieldValidator.error?.message,
        status: hexFieldValidator.error?.status,
        onChange: hexFieldValidator.handleChange,
        onBlur: () => {
          if (canvasThumbIsDragging.current) return;
          hexFieldValidator.handleBlur();
        },
        onCommit: hexFieldValidator.handleCommit,
        onCancel: hexFieldValidator.handleCancel
      })]
    }), !props.hideCustomTabActionButtons && /*#__PURE__*/_jsxs(Flow, {
      "data-testid": postfixTestId(props.testId, 'actionButtonContainer'),
      className: cn.actionButtonContainer,
      spacing: "s",
      children: [/*#__PURE__*/_jsx(Button, {
        testId: postfixTestId(props.testId, 'okButton'),
        text: localize('customTab.okBtn.label'),
        onPress: handleOkPress
      }), /*#__PURE__*/_jsx(Button, {
        testId: postfixTestId(props.testId, 'cancelButton'),
        text: localize('customTab.cancelBtn.label'),
        onPress: props.onCancelPress
      })]
    })]
  });
};
//# sourceMappingURL=CustomColorPicker.js.map